/**
 * Shared constants between Simple ↔ Builder JSON transformers
 *
 * This file contains constants used by both simple-to-builder.ts and builder-to-simple.ts
 * to ensure consistency and reduce duplication.
 */

import type { SimpleContentBlockType } from './simple-builder-schema';

// ============================================================================
// CONTENT BLOCK TYPES
// ============================================================================

/**
 * All content block types that pull data from organization profile
 */
export const CONTENT_BLOCK_TYPES: readonly SimpleContentBlockType[] = [
  'services',
  'team',
  'testimonials',
  'portfolio',
  'case_studies',
  'technologies',
  'industries',
  'pricing_packages',
  'products',
  'projects',
  'awards_recognition',
  'faqs',
  'terms',
  'legal_compliance',
  'methodology',
  'key_metrics',
  'about_us',
  'why_work_with_us',
  'social_links',
] as const;

// ============================================================================
// SMART BLOCK TYPES
// ============================================================================

/**
 * All smart block types
 */
export const SMART_BLOCK_TYPES = [
  'timeline',
  'process_flow',
  'steps',
  'arrow',
  'staircase',
  'pyramid',
  'funnel',
  'circular_arrow',
  'icon_text',
  'top_circle_boxes',
  'solid_boxes',
  'side_line_boxes',
  'quote_boxes',
] as const;

export type SmartBlockType = (typeof SMART_BLOCK_TYPES)[number];

// ============================================================================
// TEXT BLOCK TYPE MAPPINGS
// ============================================================================

/**
 * Mapping from simple text keys to Slate.js heading types
 */
export const TEXT_TYPE_MAP = {
  h1: 'heading-one',
  h2: 'heading-two',
  h3: 'heading-three',
  p: 'paragraph',
} as const;

/**
 * Reverse mapping from Slate.js types to simple text keys
 */
export const SLATE_TO_SIMPLE_MAP: Record<string, 'h1' | 'h2' | 'h3' | 'p'> = {
  'heading-one': 'h1',
  'heading-two': 'h2',
  'heading-three': 'h3',
  paragraph: 'p',
};

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Default space height in pixels
 */
export const DEFAULT_SPACE_HEIGHT = 20;

/**
 * Pixel range accepted by the lite JSON inline `[size=N]...[/size]` marker.
 * Outside it the marker is dropped and the text keeps its default size.
 */
export const LITE_INLINE_FONT_SIZE_RANGE = { min: 16, max: 96 } as const;

/**
 * Default smart block options
 */
export const SMART_BLOCK_DEFAULTS = {
  timeline: { viewMode: 'vertical' },
  pyramid: { orientation: 'up' },
  funnel: { orientation: 'down' },
} as const;

// ============================================================================
// DATA PROPERTY HELPERS
// ============================================================================

/**
 * Get block data - handles both z_data (from transformer) and data (from runtime)
 * Use this helper in both transformers to handle either data format
 */
export function getBlockData(block: any): any {
  return block?.z_data || block?.data || {};
}

/**
 * Get block items - handles both z_items (from transformer) and items (from runtime)
 * Use this helper in both transformers to handle either items format
 */
export function getBlockItems(block: any): any[] {
  return block?.z_items || block?.items || [];
}
